/**
 * DSL de campo do protocolo.
 *
 * Uma definição de mensagem produz, da MESMA fonte:
 *   - o tipo TypeScript (via Infer)
 *   - o codec binário (via codec.ts, que percorre a spec em runtime)
 *   - a documentação (via scripts/gen-docs.ts)
 *
 * Por isso tipo e fio não podem divergir. Ver ADR-0002.
 */

export type Field =
  | { readonly k: 'u8' }
  | { readonly k: 'u16' }
  | { readonly k: 'u32' }
  | { readonly k: 'i16' }
  | { readonly k: 'i32' }
  | { readonly k: 'f32' }
  | { readonly k: 'bool' }
  | { readonly k: 'str' }
  | { readonly k: 'arr'; readonly of: Field }
  | { readonly k: 'obj'; readonly fields: FieldMap };

export type FieldMap = { readonly [name: string]: Field };

export const u8 = { k: 'u8' } as const;
export const u16 = { k: 'u16' } as const;
export const u32 = { k: 'u32' } as const;
export const i16 = { k: 'i16' } as const;
export const i32 = { k: 'i32' } as const;
export const f32 = { k: 'f32' } as const;
export const bool = { k: 'bool' } as const;
export const str = { k: 'str' } as const;

export const arr = <F extends Field>(of: F) => ({ k: 'arr', of }) as const;
export const obj = <M extends FieldMap>(fields: M) => ({ k: 'obj', fields }) as const;

/** Tipo TS derivado de uma spec de campo. */
export type Infer<F extends Field> = F extends { readonly k: 'bool' }
  ? boolean
  : F extends { readonly k: 'str' }
    ? string
    : F extends { readonly k: 'arr'; readonly of: infer O extends Field }
      ? Infer<O>[]
      : F extends { readonly k: 'obj'; readonly fields: infer M extends FieldMap }
        ? { [K in keyof M]: Infer<M[K]> }
        : number;
